import type { EditorContext } from "../tools/EditorContext";
import {
  createConstructionGroup,
  dissolveConstructionGroup,
  ungroupConstructionGroups,
  ungroupConstructionLines,
  ungroupTracingImages,
  withConstructionLine,
} from "./projectOps";
import {
  descendantConstructionGroupIds,
  fullySelectedConstructionGroupIds,
  groupAncestorIds,
  nextConstructionName,
  nextConstructionOffsetName,
  nextDuplicateConstructionGroupName,
  nextDuplicateConstructionName,
  nextDuplicateTracingImageName,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  tracingImageBounds,
  type Constraint,
  type ConstraintRef,
  type ConstructionGroup,
  type ConstructionLine,
  type Project,
  type TracingImage,
} from "./Project";
import { descendantLeafIds } from "./ConstructionObject";
import {
  captureConstructionFragment,
  type ConstructionFragment,
} from "./constructionFragment";
export {
  captureConstructionFragment,
  type ConstructionFragment,
} from "./constructionFragment";
import {
  projectConstraints,
  pruneConstraintsForDeletedLines,
} from "./constraints";
import { newId } from "./ids";
import {
  translateConstructionLine,
  translateConstructionLineInProject,
} from "../tools/SelectTool";
import {
  computeArrangeDeltas,
  type ArrangeItem,
  type ArrangeOp,
} from "./arrange";
import { rotateConstructionLine } from "./constructionRotation";
import { flipConstructionLine } from "./constructionFlip";
import { flipTracingImage, rotateTracingImage } from "./tracingImageTransform";
import {
  canonicalizeConstructionLine,
  withSnappedPartnersFollowing,
} from "./constructionLineNormalize";

type SmoothAnchorEntry = NonNullable<ConstructionLine["smoothAnchors"]>[number];

/** Selection-driven group action. Mirrors the Cmd/Ctrl+G keyboard
 *  shortcut so the layers-panel "Group" button and the keystroke
 *  share one code path. Returns the new group id (or null when
 *  nothing was groupable in the current selection).
 *
 *  Lines that already belong to a selected group travel with the
 *  group, so the new wrapper holds only the loose lines plus the
 *  selected groups as nested children — same convention the keyboard
 *  shortcut documents. After commit, the new group becomes the
 *  entity selection while the wrapped line ids stay on
 *  `constructionIds` for canvas highlight. */
export function groupConstructionSelection(ctx: EditorContext): string | null {
  const sel = ctx.scene.getSelection();
  const lineIds = sel.constructionIds ?? [];
  const imageIds = sel.tracingImageIds ?? [];
  const explicitGroupIds = sel.constructionGroupIds ?? [];
  if (
    lineIds.length === 0 &&
    imageIds.length === 0 &&
    explicitGroupIds.length === 0
  ) {
    return null;
  }
  // Auto-promote: any existing group whose entire member set (lines
  // AND images, issue #509) is in the selection rides along as a
  // nested child of the new group. Without this step, selecting all
  // members of G and pressing Cmd+G would strip those members out of
  // G into the new wrapper, leaving G empty and getting pruned —
  // destroying the user's existing structure. With promotion, the new
  // wrapper holds G as a subgroup and G keeps its own members.
  const promoted = fullySelectedConstructionGroupIds(
    ctx.history.state,
    lineIds,
    imageIds,
  );
  const groupIds = Array.from(new Set([...explicitGroupIds, ...promoted]));
  const linesOwnedBySelectedGroups = new Set<string>();
  const imagesOwnedBySelectedGroups = new Set<string>();
  for (const gid of groupIds) {
    const members = descendantLeafIds(ctx.history.state, gid);
    for (const lid of members.lineIds) linesOwnedBySelectedGroups.add(lid);
    for (const iid of members.imageIds) imagesOwnedBySelectedGroups.add(iid);
  }
  const directLineIds = lineIds.filter(
    (id) => !linesOwnedBySelectedGroups.has(id),
  );
  const directImageIds = imageIds.filter(
    (id) => !imagesOwnedBySelectedGroups.has(id),
  );
  let newGroupId: string | null = null;
  ctx.commit("Group construction lines", (proj) => {
    const result = createConstructionGroup(proj, {
      lineIds: directLineIds,
      groupIds,
      imageIds: directImageIds,
    });
    newGroupId = result.groupId;
    return result.project;
  });
  if (newGroupId) {
    // Cmd+G just placed the wrapping group as the user's focus —
    // opt out of auto-promote so the post-create selection stays
    // on the new wrapper and doesn't silently re-add the children
    // groups (whose members are still in `constructionIds` and
    // would otherwise auto-promote back into the entity set).
    ctx.setSelection(
      {
        pieceId: null,
        decorationId: null,
        constructionIds: lineIds,
        constructionGroupIds: [newGroupId],
        tracingImageIds: imageIds,
      },
      { autoPromoteGroups: false },
    );
  }
  return newGroupId;
}

/** Offset applied to a duplicate's geometry relative to its original —
 *  one minor grid step along both axes (unit-aware: 10 mm metric,
 *  1/4" imperial), so a single Up then Left arrow-tap (each also a
 *  minor step, #727) walks the copy back exactly onto its original. */
function duplicateOffsetMm(ctx: EditorContext): number {
  return ctx.scene.nudgeStep("minor");
}

/** The result of inserting a fragment's clones into a project. */
export interface InsertedConstructionClones {
  project: Project;
  lineIds: string[];
  groupIds: string[];
  imageIds: string[];
  /** The fragment's selection shape mapped onto the new ids: standalone
   *  lines / images plus every member of a cloned top-level group, with
   *  the top-level group clones as the entity selection. */
  selection: {
    constructionIds: string[];
    constructionGroupIds: string[];
    tracingImageIds: string[];
  };
}

/** Insert fresh clones of a {@link ConstructionFragment} into `proj0`,
 *  offset by `offsetMm` on both axes. Every id is regenerated; the group
 *  tree, internal `offsetSource` back-links, and internal constraints are
 *  remapped onto the new ids; names continue each original's family
 *  (issue #382). Pure apart from id generation — callers run it inside a
 *  commit (Cmd+D) or against any project (clipboard paste, #763). */
export function insertConstructionFragment(
  proj0: Project,
  fragment: ConstructionFragment,
  offsetMm: number,
  scope: paper.PaperScope,
): InsertedConstructionClones {
  // Pre-allocate the id mapping so the clone passes and the returned
  // selection agree on which old id maps to which new id.
  const lineIdMap = new Map<string, string>();
  for (const l of fragment.lines) lineIdMap.set(l.id, newId("con"));
  const imageIdMap = new Map<string, string>();
  for (const i of fragment.images) imageIdMap.set(i.id, newId("timg"));
  const groupIdMap = new Map<string, string>();
  for (const g of fragment.groups) groupIdMap.set(g.id, newId("cgrp"));
  let next = proj0;
  {
    // Build the cloned line, with its `offsetSource` already remapped
    // (issue #253) so the caller can decide naming based on the new
    // back-link. Translation, primitive copy, freehand raw-points all
    // happen here so the per-pass code below stays focused on naming.
    const buildClone = (
      oldLine: ConstructionLine,
      proj: typeof next,
    ): ConstructionLine => {
      // If the line offsets from another line that is *also* being
      // duplicated, point the copy at the duplicated source so the
      // new pair stands on its own — same policy as constraint
      // cloning below. If the source is outside the duplication set,
      // drop `offsetSource` and let the copy be a free standalone
      // line frozen at the offset's current shape; otherwise the
      // next derive pass would snap it back onto the *original*
      // source.
      const remappedOffsetSource = oldLine.offsetSource
        ? lineIdMap.has(oldLine.offsetSource.sourceId)
          ? {
              ...oldLine.offsetSource,
              sourceId: lineIdMap.get(oldLine.offsetSource.sourceId)!,
            }
          : undefined
        : undefined;
      // For lines that stay derived, borrow the source's name + " (offset)"
      // — mirrors how `ConstructionTool` names a freshly-drawn offset
      // so the layers panel reads as a clear pair. For detached or
      // non-offset lines, derive the copy's name from the ORIGINAL line's
      // name (issue #382) rather than re-deriving it from the primitive /
      // kind — so a renamed "Door Panel" duplicates to "Door Panel 2"
      // instead of resetting to "Rectangle 1".
      let name: string;
      if (remappedOffsetSource) {
        const newSource = projectConstructionLines(proj).find(
          (l) => l.id === remappedOffsetSource.sourceId,
        );
        name = newSource
          ? nextConstructionOffsetName(proj, newSource)
          : nextConstructionName(proj, undefined);
      } else {
        name = nextDuplicateConstructionName(proj, oldLine.name);
      }
      // Every positional field moves with the copy — the rounding source,
      // the freehand trace, the rotation frame and pivot too. Missing one left
      // the copy tied to the ORIGINAL's position: a rounded one was re-derived
      // straight back on top of it (#1108).
      return {
        ...translateConstructionLine(oldLine, offsetMm, offsetMm, scope),
        id: lineIdMap.get(oldLine.id)!,
        name,
        offsetSource: remappedOffsetSource,
      };
    };
    // Two passes in the fragment's (project display) order. Sources (and
    // free lines) go first so auto-generated names ("Square 2",
    // "Square 3", …) come out in a predictable sequence; offsets go
    // second so they can read the FRESH name of their (now-cloned)
    // source and borrow it as "X (offset)". A chained offset-of-offset
    // still works because the captured array already has sources before
    // their derivatives — same invariant the derive pass relies on.
    for (const oldLine of fragment.lines) {
      if (oldLine.offsetSource) continue;
      next = withConstructionLine(next, buildClone(oldLine, next));
    }
    for (const oldLine of fragment.lines) {
      if (!oldLine.offsetSource) continue;
      next = withConstructionLine(next, buildClone(oldLine, next));
    }
    // Clone tracing images in display order: fresh id, a name in the
    // original's family, and offset by the same nudge so the copy reads
    // as a separate object next to the original. The raster
    // (quad/finalUrl/widthMm/heightMm/rotation/opacity) copies verbatim —
    // those are relative to x/y.
    for (const oldImage of fragment.images) {
      const newImage: TracingImage = {
        ...oldImage,
        id: imageIdMap.get(oldImage.id)!,
        name: nextDuplicateTracingImageName(next, oldImage.name),
        x: oldImage.x + offsetMm,
        y: oldImage.y + offsetMm,
        // A custom rotation pivot (#420) offsets with the duplicate so it
        // stays positioned the same way relative to the cloned image.
        ...(oldImage.pivotPoint
          ? {
              pivotPoint: {
                x: oldImage.pivotPoint.x + offsetMm,
                y: oldImage.pivotPoint.y + offsetMm,
              },
            }
          : {}),
      };
      next = {
        ...next,
        tracingImages: [...projectTracingImages(next), newImage],
      };
    }
    // Clone groups, remapping every lineId / imageId / groupId to its
    // new id so the duplicated subtree mirrors the original tree shape.
    for (const oldGroup of fragment.groups) {
      const newLineIds = oldGroup.lineIds
        .map((id) => lineIdMap.get(id))
        .filter((id): id is string => !!id);
      const newImageIds = (oldGroup.imageIds ?? [])
        .map((id) => imageIdMap.get(id))
        .filter((id): id is string => !!id);
      const newSubgroupIds = (oldGroup.groupIds ?? [])
        .map((id) => groupIdMap.get(id))
        .filter((id): id is string => !!id);
      const newGroup: ConstructionGroup = {
        id: groupIdMap.get(oldGroup.id)!,
        name: nextDuplicateConstructionGroupName(next, oldGroup.name),
        lineIds: newLineIds,
      };
      if (newSubgroupIds.length > 0) newGroup.groupIds = newSubgroupIds;
      if (newImageIds.length > 0) newGroup.imageIds = newImageIds;
      next = {
        ...next,
        constructionGroups: [...projectConstructionGroups(next), newGroup],
      };
    }
    // Clone the fragment's internal constraints (every ref points at a
    // cloned line by construction), remapped onto the new ids. Cross-set
    // constraints were already dropped at capture — pinning a copy to a
    // non-copied line would mean dragging one tugs the other.
    const newConstraints: Constraint[] = [];
    for (const c of fragment.constraints) {
      if (!c.refs.every((r) => lineIdMap.has(r.constructionId))) continue;
      const remappedRefs: ConstraintRef[] = c.refs.map((r) => ({
        ...r,
        constructionId: lineIdMap.get(r.constructionId)!,
      }));
      newConstraints.push({
        id: newId(),
        kind: c.kind,
        refs: remappedRefs,
      });
    }
    if (newConstraints.length > 0) {
      next = {
        ...next,
        constraints: [...projectConstraints(next), ...newConstraints],
      };
    }
  }
  // The fragment's selection shape on the new ids: every cloned line /
  // image lights up, with the top-level group clones entity-selected (so
  // Cmd+G / drag-as-group / another Cmd+D operates on the copies) —
  // nested clone groups stay unselected at the entity level, same shape
  // as a manual click on the outermost copy. Descendants resolve against
  // the FRAGMENT's group tree, not the live project — on a clipboard
  // paste the originals may be gone.
  const groupById = new Map(fragment.groups.map((g) => [g.id, g]));
  const descendantLineIds: string[] = [];
  const descendantImageIds: string[] = [];
  const walkGroup = (gid: string) => {
    const g = groupById.get(gid);
    if (!g) return;
    for (const lid of g.lineIds) descendantLineIds.push(lid);
    for (const iid of g.imageIds ?? []) descendantImageIds.push(iid);
    for (const sub of g.groupIds ?? []) walkGroup(sub);
  };
  for (const gid of fragment.topLevelGroupIds) walkGroup(gid);
  const mapIds = (ids: readonly string[], map: Map<string, string>) =>
    ids.map((id) => map.get(id)).filter((id): id is string => !!id);
  return {
    project: next,
    lineIds: Array.from(lineIdMap.values()),
    groupIds: Array.from(groupIdMap.values()),
    imageIds: Array.from(imageIdMap.values()),
    selection: {
      constructionIds: [
        ...mapIds(fragment.standaloneLineIds, lineIdMap),
        ...mapIds(descendantLineIds, lineIdMap),
      ],
      constructionGroupIds: mapIds(fragment.topLevelGroupIds, groupIdMap),
      tracingImageIds: [
        ...mapIds(fragment.standaloneImageIds, imageIdMap),
        ...mapIds(descendantImageIds, imageIdMap),
      ],
    },
  };
}

/** Selection-driven duplicate (Cmd+D). Captures the selection as a
 *  {@link ConstructionFragment} and inserts its clones offset by one
 *  minor grid step (or `opts.offsetMm` — the Option-drag duplicate,
 *  issue #345, passes 0 so the ensuing drag supplies the displacement).
 *  Group trees, internal offset back-links, and internal constraints are
 *  preserved on the copies; the clones become the selection so the user
 *  can immediately move / group / duplicate again. Returns the new ids
 *  (or null when the selection was empty). */
export function duplicateConstructionSelection(
  ctx: EditorContext,
  opts: { offsetMm?: number } = {},
): { lineIds: string[]; groupIds: string[]; imageIds: string[] } | null {
  const offsetMm = opts.offsetMm ?? duplicateOffsetMm(ctx);
  const fragment = captureConstructionFragment(
    ctx.history.state,
    ctx.scene.getSelection(),
  );
  if (!fragment) return null;
  const scope = ctx.scene.getPaperScope();
  let inserted: InsertedConstructionClones | null = null;
  ctx.commit("Duplicate construction", (proj0) => {
    inserted = insertConstructionFragment(proj0, fragment, offsetMm, scope);
    return inserted.project;
  });
  if (!inserted) return null;
  const done: InsertedConstructionClones = inserted;
  ctx.setSelection(
    {
      pieceId: null,
      decorationId: null,
      ...done.selection,
    },
    { autoPromoteGroups: false },
  );
  return {
    lineIds: done.lineIds,
    groupIds: done.groupIds,
    imageIds: done.imageIds,
  };
}

/** Selection-driven ungroup. Mirrors Cmd/Ctrl+Shift+G: selected
 *  groups give up their outermost wrapper, selected lines leave
 *  their parent group, empty parents pruned. No-op when the
 *  selection has nothing groupable. Returns true when a mutation
 *  was committed so callers can update button state.
 *
 *  Issue #129: ungroup removes only the **outermost** layer of
 *  grouping — never the whole chain. For each top-most selected
 *  group:
 *  - **Nested** (has a parent group) → pop it out of its parent
 *    (`ungroupConstructionGroups`). The group's own contents stay
 *    inside it; the parent prunes if that empties it. This matches
 *    the long-standing "pull this nested group out one level" intent
 *    of Cmd+Shift+G.
 *  - **Top-level** → dissolve it (`dissolveConstructionGroup`). The
 *    record disappears and its direct members + child groups
 *    promote one level up. Plain pop would be a no-op here (no
 *    parent to pop from); dissolve gives the intuitive "remove
 *    this outer layer, keep what's inside" semantic.
 *
 *  Auto-promotion (header-click sweeps every descendant group into
 *  the entity set) is neutralised by filtering to top-most: only the
 *  outermost selected group acts, descendants are left intact so a
 *  chain like `outer → inner → [A, B]` becomes `inner → [A, B]`
 *  after one ungroup, not bare `A`/`B`.
 *
 *  Lines that are descendants of any selected group are filtered out
 *  of the per-line pass — they were swept into `constructionIds` for
 *  canvas highlight, not because the user wanted them ungrouped
 *  individually. */
export function ungroupConstructionSelection(ctx: EditorContext): boolean {
  const sel = ctx.scene.getSelection();
  const allLineIds = sel.constructionIds ?? [];
  const allImageIds = sel.tracingImageIds ?? [];
  const allGroupIds = sel.constructionGroupIds ?? [];
  if (
    allLineIds.length === 0 &&
    allImageIds.length === 0 &&
    allGroupIds.length === 0
  ) {
    return false;
  }
  const proj = ctx.history.state;
  const selectedGroupSet = new Set(allGroupIds);
  const topMostGroupIds = allGroupIds.filter((gid) => {
    for (const aid of groupAncestorIds(proj, gid)) {
      if (selectedGroupSet.has(aid)) return false;
    }
    return true;
  });
  const linesOwnedBySelectedGroups = new Set<string>();
  const imagesOwnedBySelectedGroups = new Set<string>();
  for (const gid of allGroupIds) {
    const members = descendantLeafIds(proj, gid);
    for (const lid of members.lineIds) linesOwnedBySelectedGroups.add(lid);
    for (const iid of members.imageIds) imagesOwnedBySelectedGroups.add(iid);
  }
  const looseLineIds = allLineIds.filter(
    (id) => !linesOwnedBySelectedGroups.has(id),
  );
  const looseImageIds = allImageIds.filter(
    (id) => !imagesOwnedBySelectedGroups.has(id),
  );
  if (
    topMostGroupIds.length === 0 &&
    looseLineIds.length === 0 &&
    looseImageIds.length === 0
  ) {
    return false;
  }
  ctx.commit("Ungroup construction lines", (next0) => {
    let next = next0;
    for (const gid of topMostGroupIds) {
      const isTopLevel = groupAncestorIds(next, gid).length === 0;
      if (isTopLevel) {
        next = dissolveConstructionGroup(next, gid);
      } else {
        next = ungroupConstructionGroups(next, [gid]);
      }
    }
    if (looseLineIds.length > 0) {
      next = ungroupConstructionLines(next, looseLineIds);
    }
    if (looseImageIds.length > 0) {
      next = ungroupTracingImages(next, looseImageIds);
    }
    return next;
  });
  return true;
}

/** Parse a construction line's pathData into one independent
 *  2-anchor pathData string per curve segment. Returns null when the
 *  pathData fails to parse or when the line has only one segment
 *  total (nothing to split). */
function segmentPathDataForSplit(
  line: ConstructionLine,
  scope: paper.PaperScope,
): string[] | null {
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
  } catch {
    return null;
  }
  const subpaths: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  const segs: string[] = [];
  for (const sub of subpaths) {
    for (const curve of sub.curves) {
      const seg = new scope.Path({ insert: false });
      seg.add(curve.segment1.clone());
      seg.add(curve.segment2.clone());
      segs.push(seg.pathData);
      seg.remove();
    }
  }
  cp.remove();
  return segs.length >= 2 ? segs : null;
}

/** True when the line is a candidate for the Split action: any
 *  construction line whose pathData parses to ≥ 2 curve segments.
 *  Covers polylines / polygons / freehand curves AND parametric
 *  primitives whose geometry is multi-segment (rectangle, ellipse,
 *  pill). Primitive Lines parse to a single segment and naturally
 *  fall out of the segment count check — no special case needed.
 *  Splitting a primitive sheds the parametric identity by design:
 *  each segment becomes its own ConstructionLine record (no
 *  `primitive` field), then `canonicalizeConstructionLine` re-
 *  promotes the straight ones to Line primitives while the curved
 *  ones surface as "Curve N".
 *
 *  Offset-derived lines stay excluded — their pathData is recomputed
 *  from the source every commit, so any in-place edit would be
 *  silently overwritten on the next derive pass. */
export function canSplitConstructionLine(
  line: ConstructionLine,
  scope: paper.PaperScope,
): boolean {
  if (line.offsetSource) return false;
  return segmentPathDataForSplit(line, scope) !== null;
}

/** True when the current selection has at least one line that
 *  `canSplitConstructionLine` accepts. Used by `LayersPanel.render`
 *  to drive the split-polyline button's `disabled` state without
 *  duplicating the per-line check. */
export function canSplitSelection(ctx: EditorContext): boolean {
  const sel = ctx.scene.getSelection();
  const ids = sel.constructionIds ?? [];
  if (ids.length === 0) return false;
  const proj = ctx.history.state;
  const scope = ctx.scene.getPaperScope();
  const byId = new Map(projectConstructionLines(proj).map((l) => [l.id, l]));
  for (const id of ids) {
    const line = byId.get(id);
    if (line && canSplitConstructionLine(line, scope)) return true;
  }
  return false;
}

/** Selection-driven split: every selected polyline / polygon /
 *  freehand curve with ≥ 2 segments is replaced in place by one
 *  fresh construction line per segment, preserving the original's
 *  layer position and group membership. Straight 2-anchor segments
 *  are promoted to Line primitives via `canonicalizeConstructionLine`;
 *  curved segments keep `kind: "freehand"` and surface as "Curve N"
 *  in the panel. Other selected items (lines that aren't splittable,
 *  groups) are left untouched.
 *
 *  Constraints attached to a split line are dropped: per-anchor /
 *  per-curve refs carry an index into the source's `pathData` that
 *  no longer makes sense once the line has been broken into pieces,
 *  and silently re-targeting them would be worse than losing them.
 *  Offset-derived lines that follow a split source are also pruned
 *  for the same reason — the offset's geometry was a function of
 *  the whole polyline, not any one segment. */
export function splitPolylineSelection(ctx: EditorContext): boolean {
  const sel = ctx.scene.getSelection();
  const selectedIds = sel.constructionIds ?? [];
  if (selectedIds.length === 0) return false;
  const proj = ctx.history.state;
  const scope = ctx.scene.getPaperScope();
  const byId = new Map(projectConstructionLines(proj).map((l) => [l.id, l]));
  // Resolve the split set up-front so we can pre-mint the new ids
  // (needed for the post-commit selection update).
  const splits: Array<{ sourceId: string; segs: string[]; newIds: string[] }> =
    [];
  for (const id of selectedIds) {
    const line = byId.get(id);
    if (!line || !canSplitConstructionLine(line, scope)) continue;
    const segs = segmentPathDataForSplit(line, scope);
    if (!segs) continue;
    splits.push({
      sourceId: id,
      segs,
      newIds: segs.map(() => newId("con")),
    });
  }
  if (splits.length === 0) return false;
  const splitSourceIds = new Set(splits.map((s) => s.sourceId));
  ctx.commit("Split construction line", (proj0) => {
    let nextProj = proj0;
    // Build the replacement lines in the source's original slot so
    // the layers panel ordering stays put — Array#flatMap would lose
    // the per-source running project we need for unique auto-names.
    const nextLines: ConstructionLine[] = [];
    for (const cl of projectConstructionLines(proj0)) {
      if (!splitSourceIds.has(cl.id)) {
        nextLines.push(cl);
        continue;
      }
      const split = splits.find((s) => s.sourceId === cl.id)!;
      for (let i = 0; i < split.segs.length; i++) {
        const segId = split.newIds[i]!;
        const fresh: ConstructionLine = {
          id: segId,
          // Inherit visibility so a hidden polyline doesn't suddenly
          // reveal every one of its segments after the split.
          ...(cl.visible !== undefined ? { visible: cl.visible } : {}),
          pathData: split.segs[i]!,
          // Deliberately NO `smoothAnchors` carried across. Split
          // produces 2-anchor open subpaths; both their positions
          // (segment 0 = start, segment 1 = end) are open-path
          // endpoints, and a bezier-cycle "angle" / "smooth" lock
          // pairs an anchor's handleIn with its handleOut. An
          // endpoint only has one adjacent curve, so the partner
          // handle that the lock would constrain doesn't exist —
          // the entry would be meaningless metadata. Join restores
          // the lock at the merged seam from the post-stitch handle
          // geometry instead (see `detectLockMode`).
        };
        // Canonicalize: straight 2-anchor → Line primitive (auto-
        // named "Line N"); curved → no primitive, the commit
        // pipeline's `deriveConstructionLineKinds` will tag it
        // `kind: "freehand"` and rename it "Curve N".
        const canonical = canonicalizeConstructionLine(fresh, scope, {
          ...nextProj,
          constructionLines: nextLines,
        });
        nextLines.push(canonical);
        nextProj = { ...nextProj, constructionLines: nextLines };
      }
    }
    nextProj = { ...nextProj, constructionLines: nextLines };
    // Splice every new segment id into the source's slot inside any
    // group that referenced the source — preserves the user's
    // grouping intent so "split this polyline" doesn't also strip
    // the pieces out of their parent group.
    const groups = projectConstructionGroups(nextProj);
    if (groups.length > 0) {
      const nextGroups = groups.map((g) => {
        let changed = false;
        const newLineIds: string[] = [];
        for (const lid of g.lineIds) {
          const split = splits.find((s) => s.sourceId === lid);
          if (split) {
            newLineIds.push(...split.newIds);
            changed = true;
          } else {
            newLineIds.push(lid);
          }
        }
        return changed ? { ...g, lineIds: newLineIds } : g;
      });
      nextProj = { ...nextProj, constructionGroups: nextGroups };
    }
    // Drop constraints that target the source ids — see fn comment
    // for why per-curve refs can't be safely migrated.
    const prunedConstraints = pruneConstraintsForDeletedLines(
      projectConstraints(nextProj),
      splitSourceIds,
    );
    if (prunedConstraints !== projectConstraints(nextProj)) {
      nextProj = { ...nextProj, constraints: prunedConstraints };
    }
    return nextProj;
  });
  // Land the selection on the freshly created segments so the user
  // can immediately drag / delete / re-split them. Group entity
  // selection clears — the split target wasn't a group, and any
  // wrapping group of the source stays in `constructionIds` only
  // implicitly via the new line ids.
  const newSelection: string[] = [];
  for (const s of splits) newSelection.push(...s.newIds);
  ctx.setSelection({
    pieceId: null,
    decorationId: null,
    constructionIds: newSelection,
    constructionGroupIds: [],
  });
  return true;
}

/** Geometry probe used by Join: a construction line is "joinable" if
 *  its `pathData` is a single, open, non-offset subpath with ≥ 2
 *  anchors. Closed shapes (polygons, rectangles, ellipses, pills)
 *  have no endpoints to chain through, compound paths confuse the
 *  endpoint-indexing logic, and offset-derived lines re-derive
 *  their geometry from a source so editing them would silently
 *  reset on the next commit. Returns the index of the line's last
 *  segment + the start / end positions in project space, or null
 *  when the line fails any of those checks. */
function joinableLineInfo(
  line: ConstructionLine,
  scope: paper.PaperScope,
): {
  lastSegmentIndex: number;
  startPos: { x: number; y: number };
  endPos: { x: number; y: number };
} | null {
  if (line.offsetSource) return null;
  let cp: paper.CompoundPath | null = null;
  try {
    cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
  } catch {
    return null;
  }
  const subpaths: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  if (subpaths.length !== 1) {
    cp.remove();
    return null;
  }
  const sub = subpaths[0]!;
  if (sub.closed || sub.segments.length < 2) {
    cp.remove();
    return null;
  }
  const lastIdx = sub.segments.length - 1;
  const s0 = sub.segments[0]!;
  const sLast = sub.segments[lastIdx]!;
  const startPos = { x: s0.point.x, y: s0.point.y };
  const endPos = { x: sLast.point.x, y: sLast.point.y };
  cp.remove();
  return { lastSegmentIndex: lastIdx, startPos, endPos };
}

type ChainOrientation = "forward" | "reversed";
type JoinChain = {
  orderedLineIds: string[];
  orientations: ChainOrientation[];
  closed: boolean;
};

/** Minimal union-find over string keys, used by Join to bucket
 *  endpoint anchors that share a coincident-constraint partner into
 *  the same equivalence class. Each class becomes one vertex in the
 *  chain graph; each joinable line becomes an edge between its
 *  start-anchor class and end-anchor class. With that representation,
 *  detecting chains / closed cycles / branched (T-junction) regions
 *  collapses to per-vertex degree checks. */
class JoinUnionFind {
  private parent = new Map<string, string>();
  find(x: string): string {
    if (!this.parent.has(x)) {
      this.parent.set(x, x);
      return x;
    }
    let root = x;
    while (this.parent.get(root)! !== root) root = this.parent.get(root)!;
    let cur = x;
    while (this.parent.get(cur)! !== root) {
      const nxt = this.parent.get(cur)!;
      this.parent.set(cur, root);
      cur = nxt;
    }
    return root;
  }
  union(a: string, b: string): void {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent.set(ra, rb);
  }
}

function portKey(lineId: string, end: "start" | "end"): string {
  return `${lineId}|${end}`;
}

/** Endpoint-coincidence tolerance in project mm. Matches the
 *  precision the rest of the construction stack uses for "the same
 *  point" comparisons (e.g. `canonicalizeConstructionLine`'s
 *  endpoint check, the broken-constraint detector). Lines whose
 *  endpoints land within this distance are treated as snapped
 *  together for the purpose of building a join chain, even if the
 *  user never explicitly created a coincident constraint between
 *  them — covers the grid-snap and free-draw-onto-existing-vertex
 *  cases where the geometry already lines up. */
const JOIN_POSITION_EPS_MM = 0.001;

/** Build join chains from a set of joinable construction lines and
 *  the project's coincident constraints.
 *
 *  Algorithm:
 *   1. Each joinable line's two endpoints become two "ports". A port
 *      is a `(lineId, "start"|"end")` pair.
 *   2. Every coincident constraint whose refs are *all* endpoint
 *      anchors of *joinable* lines unions those ports in a union-find.
 *      Constraints referencing interior anchors or non-joinable lines
 *      are ignored: they're not coalescing the endpoints we'd merge
 *      into a single polyline anchor.
 *   3. Endpoint *positions* that match within `JOIN_POSITION_EPS_MM`
 *      are unioned too, so lines whose endpoints snapped to the same
 *      grid point (or were drawn onto an existing vertex without the
 *      user pulling the snap-constraint toggle) chain the same way as
 *      lines pinned by a saved constraint. The constraint-based pass
 *      runs first so a constraint with refs at *not-quite-equal*
 *      positions still merges its ports.
 *   4. Equivalence classes are the vertices of a multigraph; each
 *      joinable line is an edge connecting its start-port's class to
 *      its end-port's class.
 *   5. Per connected component, validate that every vertex has degree
 *      ≤ 2 (else it's a branched T-junction we won't try to resolve)
 *      and no line is a self-loop (start ≈ end). Skip components
 *      that fail either check.
 *   6. Walk each valid component as a path: start from a degree-1
 *      vertex (open chain) or, when all vertices are degree 2 (closed
 *      cycle), from any line; orient each step so the entry port is
 *      the next line's "start" side.
 *   7. Single-line components (selection has only one joinable line
 *      in this group) are dropped — there's no second line to merge
 *      with. */
function buildJoinChains(
  joinables: Map<
    string,
    {
      lastSegmentIndex: number;
      startPos: { x: number; y: number };
      endPos: { x: number; y: number };
    }
  >,
  constraints: ReadonlyArray<Constraint>,
): JoinChain[] {
  const uf = new JoinUnionFind();
  for (const id of joinables.keys()) {
    uf.find(portKey(id, "start"));
    uf.find(portKey(id, "end"));
  }
  for (const c of constraints) {
    if (c.kind !== "coincident") continue;
    const ports: string[] = [];
    let allEndpointsOfJoinables = true;
    for (const ref of c.refs) {
      if (ref.kind !== "anchor") {
        allEndpointsOfJoinables = false;
        break;
      }
      const info = joinables.get(ref.constructionId);
      if (!info) {
        allEndpointsOfJoinables = false;
        break;
      }
      if (ref.pathIndex !== 0) {
        allEndpointsOfJoinables = false;
        break;
      }
      let end: "start" | "end" | null = null;
      if (ref.segmentIndex === 0) end = "start";
      else if (ref.segmentIndex === info.lastSegmentIndex) end = "end";
      if (!end) {
        allEndpointsOfJoinables = false;
        break;
      }
      ports.push(portKey(ref.constructionId, end));
    }
    if (!allEndpointsOfJoinables || ports.length < 2) continue;
    for (let i = 1; i < ports.length; i++) uf.union(ports[0]!, ports[i]!);
  }
  // Position-based union: any two endpoints that land at the same
  // point (within tolerance) get the same vertex class. O(N²) over
  // endpoint ports is fine — a layers selection rarely exceeds a few
  // dozen lines and the constant is small.
  const portList: Array<{
    key: string;
    pos: { x: number; y: number };
  }> = [];
  for (const [id, info] of joinables) {
    portList.push({ key: portKey(id, "start"), pos: info.startPos });
    portList.push({ key: portKey(id, "end"), pos: info.endPos });
  }
  for (let i = 0; i < portList.length; i++) {
    for (let j = i + 1; j < portList.length; j++) {
      const a = portList[i]!;
      const b = portList[j]!;
      if (
        Math.abs(a.pos.x - b.pos.x) <= JOIN_POSITION_EPS_MM &&
        Math.abs(a.pos.y - b.pos.y) <= JOIN_POSITION_EPS_MM
      ) {
        uf.union(a.key, b.key);
      }
    }
  }
  // Multigraph: vertex (class root) -> list of line incidences.
  const lineVerts = new Map<string, { startV: string; endV: string }>();
  const edgesByVertex = new Map<
    string,
    Array<{ lineId: string; end: "start" | "end" }>
  >();
  for (const id of joinables.keys()) {
    const startV = uf.find(portKey(id, "start"));
    const endV = uf.find(portKey(id, "end"));
    lineVerts.set(id, { startV, endV });
    if (!edgesByVertex.has(startV)) edgesByVertex.set(startV, []);
    if (!edgesByVertex.has(endV)) edgesByVertex.set(endV, []);
    edgesByVertex.get(startV)!.push({ lineId: id, end: "start" });
    edgesByVertex.get(endV)!.push({ lineId: id, end: "end" });
  }
  const visited = new Set<string>();
  const chains: JoinChain[] = [];
  for (const startId of joinables.keys()) {
    if (visited.has(startId)) continue;
    // BFS the connected component.
    const component: string[] = [];
    const seen = new Set<string>([startId]);
    const queue: string[] = [startId];
    while (queue.length > 0) {
      const lid = queue.shift()!;
      component.push(lid);
      const { startV, endV } = lineVerts.get(lid)!;
      for (const v of [startV, endV]) {
        for (const inc of edgesByVertex.get(v)!) {
          if (!seen.has(inc.lineId)) {
            seen.add(inc.lineId);
            queue.push(inc.lineId);
          }
        }
      }
    }
    for (const lid of component) visited.add(lid);
    if (component.length < 2) continue;
    // Branch check: every vertex incident to component must have degree ≤ 2.
    let branched = false;
    const compVerts = new Set<string>();
    for (const lid of component) {
      const { startV, endV } = lineVerts.get(lid)!;
      compVerts.add(startV);
      compVerts.add(endV);
    }
    for (const v of compVerts) {
      if (edgesByVertex.get(v)!.length > 2) {
        branched = true;
        break;
      }
    }
    if (branched) continue;
    // Self-loop check: a line whose start vertex == end vertex would
    // need to fold onto itself. Skip the whole component.
    let selfLoop = false;
    for (const lid of component) {
      const { startV, endV } = lineVerts.get(lid)!;
      if (startV === endV) {
        selfLoop = true;
        break;
      }
    }
    if (selfLoop) continue;
    // Find a degree-1 starting vertex; absence → closed cycle.
    let firstLid: string | null = null;
    let firstOri: ChainOrientation = "forward";
    let closed = true;
    for (const lid of component) {
      const { startV, endV } = lineVerts.get(lid)!;
      const sDeg = edgesByVertex.get(startV)!.length;
      const eDeg = edgesByVertex.get(endV)!.length;
      if (sDeg === 1) {
        firstLid = lid;
        firstOri = "forward";
        closed = false;
        break;
      }
      if (eDeg === 1) {
        firstLid = lid;
        firstOri = "reversed";
        closed = false;
        break;
      }
    }
    if (firstLid === null) {
      firstLid = component[0]!;
      firstOri = "forward";
      closed = true;
    }
    // Walk the chain.
    const orderedLineIds: string[] = [firstLid];
    const orientations: ChainOrientation[] = [firstOri];
    let currLid = firstLid;
    let currOri = firstOri;
    while (orderedLineIds.length < component.length) {
      const { startV, endV } = lineVerts.get(currLid)!;
      const exitV = currOri === "forward" ? endV : startV;
      const next = edgesByVertex
        .get(exitV)!
        .find((inc) => inc.lineId !== currLid);
      if (!next) break;
      orderedLineIds.push(next.lineId);
      orientations.push(next.end === "start" ? "forward" : "reversed");
      currLid = next.lineId;
      currOri = next.end === "start" ? "forward" : "reversed";
    }
    chains.push({ orderedLineIds, orientations, closed });
  }
  return chains;
}

/** Map a single anchor on a consumed line to its index in the merged
 *  path's segment list, given the line's chain position, orientation,
 *  and the cumulative merged-offset where this line's NON-SEAM
 *  contribution starts. Returns the merged segmentIndex — for chain
 *  positions ≥ 1, the seam anchor maps to `offsetInMerged - 1`
 *  (where the previous line's last segment already sits), which lets
 *  the join side dedupe seam entries that arrive from both sides. */
function mergedSegmentIndex(
  origIndex: number,
  ori: ChainOrientation,
  origSegCount: number,
  chainPos: number,
  offsetInMerged: number,
): number {
  if (chainPos === 0) {
    return ori === "forward" ? origIndex : origSegCount - 1 - origIndex;
  }
  if (ori === "forward") {
    // First original segment is the seam (skipped); rest land
    // immediately after offsetInMerged.
    if (origIndex === 0) return offsetInMerged - 1;
    return offsetInMerged + (origIndex - 1);
  }
  // Reversed: last original segment is the seam (skipped); the rest
  // walk back to the line's start.
  if (origIndex === origSegCount - 1) return offsetInMerged - 1;
  return offsetInMerged + (origSegCount - 1 - origIndex - 1);
}

/** Geometric heuristic for re-deriving bezier-cycle lock state from
 *  the post-stitch handle pair at an interior anchor. The
 *  bezier-cycle's "smooth" / "angle" lock invariants are:
 *
 *   - "smooth"  → handleIn and handleOut are antiparallel AND equal in
 *                 magnitude (handleOut = −handleIn).
 *   - "angle"   → handleIn and handleOut are antiparallel, magnitudes
 *                 may differ (handleOut = −k·handleIn, k > 0).
 *   - none      → handles are independent (non-collinear, parallel
 *                 same-direction, or one side is zero).
 *
 *  Join lost the explicit lock entry on the way through Split (every
 *  piece anchor was an endpoint, so the lock was meaningless to
 *  carry — see the Split invariant test). This heuristic looks at
 *  the geometry alone to decide whether a merged interior anchor
 *  *deserves* a lock now that it sits between two adjacent curves:
 *  if the handles match the lock's geometric definition within
 *  tolerance, we promote the anchor up one rung of the cycle.
 *
 *  Tolerances:
 *   - `SIN_TOL` (1e-3) on `|cross| / (|in|·|out|)` ≈ 0.057° of
 *     collinearity slack — tight enough that "independent" handles
 *     don't get falsely upgraded, loose enough to survive paper.js
 *     serialize/parse round-trip error.
 *   - `MAG_REL_TOL` (1e-3) on `|in| − |out|` relative to the
 *     larger — same ~0.1% relative tolerance for the magnitude
 *     match that distinguishes smooth from angle.
 *
 *  Both handles must be non-trivial (≥ MAG_ABS_MIN). A one-sided
 *  tangent (handleIn = 0 or handleOut = 0) describes a curve-to-line
 *  transition, not a tangent through the anchor, so it's never
 *  lockable. */
function detectLockMode(
  handleIn: { x: number; y: number },
  handleOut: { x: number; y: number },
): "angle" | "smooth" | undefined {
  const MAG_ABS_MIN = 1e-6;
  const SIN_TOL = 1e-3;
  const MAG_REL_TOL = 1e-3;
  const inMag = Math.hypot(handleIn.x, handleIn.y);
  const outMag = Math.hypot(handleOut.x, handleOut.y);
  if (inMag < MAG_ABS_MIN || outMag < MAG_ABS_MIN) return undefined;
  // cross = |a||b| sin(θ). Collinear ⇒ |sin(θ)| < SIN_TOL.
  const cross = handleIn.x * handleOut.y - handleIn.y * handleOut.x;
  if (Math.abs(cross) > SIN_TOL * inMag * outMag) return undefined;
  // dot < 0 ⇒ handles point in opposite directions (the tangent
  // actually passes through the anchor instead of folding back).
  const dot = handleIn.x * handleOut.x + handleIn.y * handleOut.y;
  if (dot >= 0) return undefined;
  const magDiff = Math.abs(inMag - outMag);
  if (magDiff <= MAG_REL_TOL * Math.max(inMag, outMag)) return "smooth";
  return "angle";
}

/** Build the merged pathData for a single chain by walking each line
 *  in order, reversing segments when the chain entered via the
 *  line's end-port, and concatenating into one Paper.js Path. The
 *  first segment of every line after the first is dropped because it
 *  duplicates the previous line's end anchor (the constraint that
 *  bound the chain made them coincident); skipping it keeps the
 *  merged path's anchor count one less than the naive sum.
 *
 *  Returns the joined pathData together with the rebuilt
 *  `smoothAnchors` entries. The lock-state mapping walks each
 *  consumed line's own `smoothAnchors`, projects each entry through
 *  `mergedSegmentIndex` (which respects the chain orientation and
 *  the duplicate-seam skip), and dedupes when the same merged anchor
 *  picks up the same entry from both sides of a seam — by
 *  construction Split duplicates the lock to both adjacent pieces,
 *  so either side suffices to recover it on join. */
function buildJoinedPathData(
  chain: JoinChain,
  linesById: Map<string, ConstructionLine>,
  scope: paper.PaperScope,
): { pathData: string; smoothAnchors: SmoothAnchorEntry[] } | null {
  const merged = new scope.Path({ insert: false });
  // Per-line metadata captured while walking the chain — we need the
  // original segment count + the merged-offset at which each line's
  // non-seam contribution starts so we can project its smoothAnchors
  // through `mergedSegmentIndex` after the geometry is stitched.
  const lineMeta: Array<{ origSegCount: number; offsetInMerged: number }> = [];
  for (let i = 0; i < chain.orderedLineIds.length; i++) {
    const lid = chain.orderedLineIds[i]!;
    const ori = chain.orientations[i]!;
    const line = linesById.get(lid);
    if (!line) {
      merged.remove();
      return null;
    }
    let cp: paper.CompoundPath | null = null;
    try {
      cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
    } catch {
      merged.remove();
      return null;
    }
    const subpaths: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    if (subpaths.length !== 1) {
      cp.remove();
      merged.remove();
      return null;
    }
    const sub = subpaths[0]!;
    const origSegCount = sub.segments.length;
    let segs = sub.segments.map((s) => s.clone());
    if (ori === "reversed") {
      segs = segs.reverse().map((s) => {
        const swapped = s.clone();
        const inHandle = swapped.handleIn.clone();
        swapped.handleIn = swapped.handleOut;
        swapped.handleOut = inHandle;
        return swapped;
      });
    }
    const offsetInMerged = merged.segments.length;
    lineMeta.push({ origSegCount, offsetInMerged });
    if (i === 0) {
      for (const s of segs) merged.add(s);
    } else {
      // Seam vertex (the last segment already in `merged`) needs its
      // outgoing-side bezier handle recovered from the line we're
      // splicing in. Open-path SVG serialization drops the trailing
      // `handleOut` of a path's last segment (there's no curve after
      // it to encode), so the previous line's last segment lands here
      // with handleOut = 0 even when the original geometry — before
      // the path was ever serialized — had a tangent there. The line
      // we're joining still carries that data on its *first* segment:
      // that segment's `handleOut` becomes control1 of its first
      // curve when serialized, so it survives the round trip. Pull it
      // across before skipping the duplicate seam segment, and the
      // bezier curve at the join point is reconstructed exactly.
      // Without this step a polyline split into two pieces and glued
      // back together loses one of the two control points at the cut
      // vertex and the merged curve flattens visibly.
      if (merged.segments.length > 0) {
        const seam = merged.segments[merged.segments.length - 1]!;
        seam.handleOut = segs[0]!.handleOut.clone();
      }
      for (let k = 1; k < segs.length; k++) merged.add(segs[k]!);
    }
    cp.remove();
  }
  if (chain.closed) merged.closed = true;
  const pathData = merged.pathData;
  // Rebuild smoothAnchors from each consumed line. Both sides of any
  // seam carry the same lock entry (Split duplicated it), so we
  // dedupe by merged segmentIndex on the way out. Stable mode
  // resolution: keep the first value seen for any given anchor —
  // matches Split's "all pieces share the same mode at the seam"
  // contract and is defensive against an inconsistent input.
  const byMergedIndex = new Map<number, SmoothAnchorEntry>();
  for (let i = 0; i < chain.orderedLineIds.length; i++) {
    const line = linesById.get(chain.orderedLineIds[i]!);
    if (!line || !line.smoothAnchors) continue;
    const ori = chain.orientations[i]!;
    const meta = lineMeta[i]!;
    for (const e of line.smoothAnchors) {
      if (e.pathIndex !== 0) continue;
      const mergedIdx = mergedSegmentIndex(
        e.segmentIndex,
        ori,
        meta.origSegCount,
        i,
        meta.offsetInMerged,
      );
      if (mergedIdx < 0 || mergedIdx >= merged.segments.length) continue;
      if (byMergedIndex.has(mergedIdx)) continue;
      byMergedIndex.set(mergedIdx, {
        pathIndex: 0,
        segmentIndex: mergedIdx,
        ...(e.mode !== undefined ? { mode: e.mode } : {}),
      });
    }
  }
  // Geometric heuristic: for every merged interior anchor that
  // doesn't already have an explicit entry from the consumed lines,
  // inspect the actual handle pair and infer a lock if the geometry
  // satisfies the bezier-cycle's "smooth" / "angle" invariant.
  //
  // This is what makes the Split → Join round trip preserve the user's
  // lock state even though Split deliberately drops every smoothAnchors
  // entry (its pieces' anchors are all open-path endpoints — see the
  // Split invariant test). The lock was encoded in the handle geometry
  // anyway (it's *what* the lock means), so reading it back at join
  // time is lossless for the canonical Split → Join case.
  //
  // For chains assembled from unrelated hand-drawn lines that happen
  // to meet smoothly at the seam, this also makes the merged polyline
  // pick up the smoothness lock automatically — a useful UX side
  // effect: dragging one side of the seam after the join keeps the
  // tangent through the join point as the user originally drew it.
  //
  // Open chains: only segments 1..len-2 are interior (endpoints have
  // a single adjacent curve and can't carry a lock). Closed chains:
  // every segment is interior because the closing edge brings the
  // missing curve to anchors 0 and last.
  const interiorRange: number[] = [];
  const totalSegs = merged.segments.length;
  if (chain.closed) {
    for (let k = 0; k < totalSegs; k++) interiorRange.push(k);
  } else {
    for (let k = 1; k < totalSegs - 1; k++) interiorRange.push(k);
  }
  for (const idx of interiorRange) {
    if (byMergedIndex.has(idx)) continue;
    const seg = merged.segments[idx]!;
    const mode = detectLockMode(seg.handleIn, seg.handleOut);
    if (!mode) continue;
    byMergedIndex.set(idx, {
      pathIndex: 0,
      segmentIndex: idx,
      mode,
    });
  }
  const smoothAnchors = Array.from(byMergedIndex.values()).sort(
    (a, b) => a.segmentIndex - b.segmentIndex,
  );
  merged.remove();
  return { pathData, smoothAnchors };
}

/** Inner reusable: compute the chains for the current selection.
 *  Returns the join input (lines, chains, consumed-id set) so both
 *  `canJoinSelection` and `joinConstructionSelection` can run the
 *  same predicate without duplicating the graph-building logic. */
function planJoinForSelection(ctx: EditorContext): {
  joinables: Map<string, { line: ConstructionLine; lastSegmentIndex: number }>;
  chains: JoinChain[];
} {
  const proj = ctx.history.state;
  const scope = ctx.scene.getPaperScope();
  const selectedIds = ctx.scene.getSelection().constructionIds ?? [];
  const linesById = new Map(
    projectConstructionLines(proj).map((l) => [l.id, l]),
  );
  const joinables = new Map<
    string,
    { line: ConstructionLine; lastSegmentIndex: number }
  >();
  const graphInput = new Map<
    string,
    {
      lastSegmentIndex: number;
      startPos: { x: number; y: number };
      endPos: { x: number; y: number };
    }
  >();
  for (const id of selectedIds) {
    const line = linesById.get(id);
    if (!line) continue;
    const info = joinableLineInfo(line, scope);
    if (!info) continue;
    joinables.set(id, { line, lastSegmentIndex: info.lastSegmentIndex });
    graphInput.set(id, {
      lastSegmentIndex: info.lastSegmentIndex,
      startPos: info.startPos,
      endPos: info.endPos,
    });
  }
  const chains =
    joinables.size < 2
      ? []
      : buildJoinChains(graphInput, projectConstraints(proj));
  return { joinables, chains };
}

/** True when the current selection has at least one joinable chain
 *  (two or more open construction lines linked end-to-end via
 *  coincident constraints). Drives the layers-header Join button's
 *  `disabled` state without duplicating the chain-builder logic. */
export function canJoinSelection(ctx: EditorContext): boolean {
  const { chains } = planJoinForSelection(ctx);
  return chains.length > 0;
}

/** Selection-driven Join: collapse every chain of endpoint-snapped
 *  open construction lines in the selection into a single polyline
 *  (or closed polyline / polygon when the chain wraps back on
 *  itself). The first consumed line's slot in the layers list and
 *  its group membership are preserved — the new polyline lands where
 *  that line was, with the remaining chain members removed.
 *
 *  Visibility inherits the most-restrictive setting: if any line in
 *  the chain is hidden, the merged polyline is hidden too. Otherwise
 *  the new line stays visible.
 *
 *  Constraints touching any consumed line are dropped. The
 *  endpoint-coincident ones are absorbed into the polyline by
 *  construction (the merged anchor is one point), and per-anchor /
 *  per-curve refs targeting interior segments of a consumed line
 *  carry index numbers that no longer make sense once the geometry
 *  has been spliced — silently re-targeting them would be worse than
 *  losing them. Same reasoning the Split action uses; see
 *  `splitPolylineSelection`. Offset-derived lines following a
 *  consumed source are pruned for the same reason — the source no
 *  longer exists. */
export function joinConstructionSelection(ctx: EditorContext): boolean {
  const { joinables, chains } = planJoinForSelection(ctx);
  if (chains.length === 0) return false;
  const scope = ctx.scene.getPaperScope();
  const newIds = chains.map(() => newId("con"));
  const consumedByChain: Set<string>[] = chains.map(
    (c) => new Set(c.orderedLineIds),
  );
  const consumedAll = new Set<string>();
  for (const set of consumedByChain) for (const id of set) consumedAll.add(id);
  // Pre-compute new pathData per chain so a parse failure in any
  // chain aborts the whole commit before mutating the project.
  const linesByIdAtPlan = new Map(
    Array.from(joinables.entries()).map(([id, v]) => [id, v.line]),
  );
  const builtPaths: Array<{
    pathData: string;
    smoothAnchors: SmoothAnchorEntry[];
  }> = [];
  for (const chain of chains) {
    const built = buildJoinedPathData(chain, linesByIdAtPlan, scope);
    if (built === null) return false;
    builtPaths.push(built);
  }
  ctx.commit("Join construction lines", (proj0) => {
    let nextProj = proj0;
    const allLinesList = projectConstructionLines(proj0);
    const nextLines: ConstructionLine[] = [];
    const insertedChainIdx = new Set<number>();
    for (const cl of allLinesList) {
      if (!consumedAll.has(cl.id)) {
        nextLines.push(cl);
        continue;
      }
      let chainIdx = -1;
      for (let ci = 0; ci < chains.length; ci++) {
        if (consumedByChain[ci]!.has(cl.id)) {
          chainIdx = ci;
          break;
        }
      }
      if (chainIdx < 0) {
        nextLines.push(cl);
        continue;
      }
      if (insertedChainIdx.has(chainIdx)) continue;
      const chain = chains[chainIdx]!;
      const anyHidden = chain.orderedLineIds.some(
        (id) => linesByIdAtPlan.get(id)?.visible === false,
      );
      const modifier = chain.closed ? "Polygon" : "Polyline";
      const built = builtPaths[chainIdx]!;
      const fresh: ConstructionLine = {
        id: newIds[chainIdx]!,
        name: nextConstructionName(
          { ...nextProj, constructionLines: nextLines },
          modifier,
        ),
        pathData: built.pathData,
        kind: chain.closed ? "polygon" : "polyline",
        ...(anyHidden ? { visible: false } : {}),
        // Rebuilt lock-state from the consumed lines'
        // `smoothAnchors` — see `buildJoinedPathData` for the
        // index remap. Empty array suppressed so projects without
        // any locks don't pick up an empty field.
        ...(built.smoothAnchors.length > 0
          ? { smoothAnchors: built.smoothAnchors }
          : {}),
      };
      nextLines.push(fresh);
      insertedChainIdx.add(chainIdx);
    }
    nextProj = { ...nextProj, constructionLines: nextLines };
    // Group rewrite: substitute the consumed line ids with the
    // chain's new polyline id at the first occurrence; later
    // occurrences are dropped so the group doesn't carry the same
    // new id twice. Groups that contained none of the consumed lines
    // pass through unchanged.
    const groups = projectConstructionGroups(nextProj);
    if (groups.length > 0) {
      const nextGroups = groups.map((g) => {
        let changed = false;
        const newLineIds: string[] = [];
        const seenNew = new Set<string>();
        for (const lid of g.lineIds) {
          let chainIdx = -1;
          for (let ci = 0; ci < chains.length; ci++) {
            if (consumedByChain[ci]!.has(lid)) {
              chainIdx = ci;
              break;
            }
          }
          if (chainIdx < 0) {
            newLineIds.push(lid);
            continue;
          }
          changed = true;
          const nid = newIds[chainIdx]!;
          if (!seenNew.has(nid)) {
            newLineIds.push(nid);
            seenNew.add(nid);
          }
        }
        return changed ? { ...g, lineIds: newLineIds } : g;
      });
      nextProj = { ...nextProj, constructionGroups: nextGroups };
    }
    const pruned = pruneConstraintsForDeletedLines(
      projectConstraints(nextProj),
      consumedAll,
    );
    if (pruned !== projectConstraints(nextProj)) {
      nextProj = { ...nextProj, constraints: pruned };
    }
    return nextProj;
  });
  ctx.setSelection({
    pieceId: null,
    decorationId: null,
    constructionIds: newIds,
    constructionGroupIds: [],
  });
  return true;
}

/** One arrangeable object: a stable key plus the construction-line and
 *  tracing-image ids that translate together when the object moves. */
export interface ArrangeableObject {
  key: string;
  lineIds: string[];
  imageIds: string[];
}

/** Resolve the current selection into the set of independently
 *  arrangeable objects (issue #115). Each *top-level* selected group is
 *  one object whose member lines (recursively, through any subgroups)
 *  all move together; each loose selected line not owned by such a
 *  group is its own object.
 *
 *  Only top-level groups become objects: when the selection includes a
 *  group AND one of its subgroups (auto-promotion does exactly this
 *  when every member is selected), the subgroup must ride inside its
 *  ancestor's object — otherwise its lines would be translated once per
 *  enclosing object and the group would break apart (#115 follow-up). */
export function arrangeableConstructionObjects(
  project: Project,
  sel: {
    constructionIds?: string[];
    constructionGroupIds?: string[];
    tracingImageIds?: string[];
  },
): ArrangeableObject[] {
  const lineIds = sel.constructionIds ?? [];
  const imageIds = sel.tracingImageIds ?? [];
  const groupIds = sel.constructionGroupIds ?? [];
  const topLevelGroupIds = groupIds.filter((gid) => {
    const ancestors = groupAncestorIds(project, gid);
    return !ancestors.some((aid) => groupIds.includes(aid));
  });
  const objects: ArrangeableObject[] = [];
  const ownedLines = new Set<string>();
  const ownedImages = new Set<string>();
  for (const gid of topLevelGroupIds) {
    const { lineIds: memberLines, imageIds: memberImages } = descendantLeafIds(
      project,
      gid,
    );
    for (const m of memberLines) ownedLines.add(m);
    for (const m of memberImages) ownedImages.add(m);
    if (memberLines.length > 0 || memberImages.length > 0) {
      objects.push({
        key: `group:${gid}`,
        lineIds: memberLines,
        imageIds: memberImages,
      });
    }
  }
  for (const lid of lineIds) {
    if (ownedLines.has(lid)) continue;
    objects.push({ key: `line:${lid}`, lineIds: [lid], imageIds: [] });
  }
  for (const iid of imageIds) {
    if (ownedImages.has(iid)) continue;
    objects.push({ key: `image:${iid}`, lineIds: [], imageIds: [iid] });
  }
  return objects;
}

/** The arrangeable object that anchors align / centre — the FIRST-selected
 *  one (#872). Each selection array preserves add-order (shift-click
 *  appends; the LEAD is the last entry), so the earliest entry is the
 *  first-selected within its category. The object owning the first-selected
 *  line wins (an auto-promoted group owns its member lines, so selecting
 *  its lines anchors the group); with no line selected, the first group,
 *  then the first tracing image. Null (union fallback) when nothing
 *  matches. */
function firstSelectedArrangeKey(
  sel: {
    constructionIds?: string[];
    constructionGroupIds?: string[];
    tracingImageIds?: string[];
  },
  objects: ReadonlyArray<ArrangeableObject>,
): string | null {
  const lid = (sel.constructionIds ?? [])[0];
  if (lid) {
    const owner = objects.find((o) => o.lineIds.includes(lid));
    if (owner) return owner.key;
  }
  for (const gid of sel.constructionGroupIds ?? []) {
    const owner = objects.find((o) => o.key === `group:${gid}`);
    if (owner) return owner.key;
  }
  const iid = (sel.tracingImageIds ?? [])[0];
  if (iid) {
    const owner = objects.find((o) => o.imageIds.includes(iid));
    if (owner) return owner.key;
  }
  return null;
}

/** Arrange (align / centre / distribute) the current construction
 *  selection (issue #115). Bounds are read from the rendered items so
 *  the math frames against exactly what the user sees. Align / centre
 *  anchor on the first-selected object (#872) — it stays put and the
 *  others come to it; distribute frames against the selection union.
 *  Returns true when a move was committed; a no-op (false) for fewer
 *  than two arrangeable objects or when the operation leaves everything
 *  in place. The selection is left intact so operations can be
 *  chained. */
export function arrangeConstructionSelection(
  ctx: EditorContext,
  op: ArrangeOp,
): boolean {
  const sel = ctx.scene.getSelection();
  const objects = arrangeableConstructionObjects(ctx.history.state, sel);
  if (objects.length < 2) return false;

  // Union bounds per object from the rendered items (project coords).
  // Accumulate min/max directly to avoid depending on paper.js types.
  // Lines read from the rendered item; images use their model bounds
  // (`tracingImageBounds`), which is the same rotated AABB a line's
  // rendered item reports.
  const imagesById = new Map(
    projectTracingImages(ctx.history.state).map((img) => [img.id, img]),
  );
  const items: ArrangeItem[] = [];
  const lineIdsByKey = new Map<string, string[]>();
  const imageIdsByKey = new Map<string, string[]>();
  for (const obj of objects) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let found = false;
    const fold = (b: {
      x: number;
      y: number;
      width: number;
      height: number;
    }) => {
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.width);
      maxY = Math.max(maxY, b.y + b.height);
      found = true;
    };
    for (const lid of obj.lineIds) {
      const item = ctx.scene.getConstructionLineItem(lid);
      if (item) fold(item.bounds);
    }
    for (const iid of obj.imageIds) {
      const img = imagesById.get(iid);
      if (img) fold(tracingImageBounds(img));
    }
    if (!found) continue;
    items.push({
      id: obj.key,
      bounds: { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
    });
    lineIdsByKey.set(obj.key, obj.lineIds);
    imageIdsByKey.set(obj.key, obj.imageIds);
  }
  if (items.length < 2) return false;

  const moves = computeArrangeDeltas(
    items,
    op,
    firstSelectedArrangeKey(sel, objects),
  ).filter((d) => d.dx !== 0 || d.dy !== 0);
  if (moves.length === 0) return false;

  const scope = ctx.scene.getPaperScope();
  ctx.commit(`Arrange construction objects (${op})`, (proj) => {
    let next = proj;
    const imageDelta = new Map<string, { dx: number; dy: number }>();
    for (const d of moves) {
      for (const lid of lineIdsByKey.get(d.id) ?? []) {
        next = translateConstructionLineInProject(next, lid, d.dx, d.dy, scope);
      }
      for (const iid of imageIdsByKey.get(d.id) ?? []) {
        imageDelta.set(iid, { dx: d.dx, dy: d.dy });
      }
    }
    if (imageDelta.size > 0) {
      next = {
        ...next,
        tracingImages: projectTracingImages(next).map((img) => {
          const m = imageDelta.get(img.id);
          if (!m) return img;
          return {
            ...img,
            x: img.x + m.dx,
            y: img.y + m.dy,
            // Custom rotation pivot (#420) rides along on an arrange move.
            ...(img.pivotPoint
              ? {
                  pivotPoint: {
                    x: img.pivotPoint.x + m.dx,
                    y: img.pivotPoint.y + m.dy,
                  },
                }
              : {}),
          };
        }),
      };
    }
    return next;
  });
  return true;
}

/** Flip / rotate-90 the current construction selection in place about
 *  the centre of its combined bounding box (issue #346). Works with any
 *  non-empty selection — a single object is fine. Every selected line
 *  (loose lines plus every member of every selected group, deduped)
 *  transforms about the one shared pivot, so the selection keeps its
 *  relative layout and the whole thing reads as one rigid flip/turn.
 *  Bounds come from the rendered items. Returns true when a transform
 *  was committed. The selection is left intact so ops can be chained. */
export type ConstructionTransformOp =
  | "flip-horizontal"
  | "flip-vertical"
  | "rotate-left"
  | "rotate-right";

export function transformConstructionSelection(
  ctx: EditorContext,
  op: ConstructionTransformOp,
): boolean {
  const sel = ctx.scene.getSelection();
  // Flatten the selection to the sets of line ids and image ids to
  // transform. A Set dedupes objects reached through both an explicit
  // selection axis and a selected (possibly nested) group, so each
  // transforms exactly once.
  const lineIds = new Set<string>(sel.constructionIds ?? []);
  const imageIds = new Set<string>(sel.tracingImageIds ?? []);
  for (const gid of sel.constructionGroupIds ?? []) {
    const members = descendantLeafIds(ctx.history.state, gid);
    for (const lid of members.lineIds) lineIds.add(lid);
    for (const iid of members.imageIds) imageIds.add(iid);
  }
  if (lineIds.size === 0 && imageIds.size === 0) return false;

  // Combined bounding box (project coords) of the rendered items — lines
  // via their Paper `bounds`, images via the rotated AABB so the pivot is
  // the centre of everything selected, matching how arrange measures them.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let found = false;
  for (const lid of lineIds) {
    const item = ctx.scene.getConstructionLineItem(lid);
    if (!item) continue;
    const b = item.bounds;
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
    found = true;
  }
  if (imageIds.size > 0) {
    const byId = new Map(
      projectTracingImages(ctx.history.state).map((img) => [img.id, img]),
    );
    for (const iid of imageIds) {
      const img = byId.get(iid);
      if (!img) continue;
      const b = tracingImageBounds(img);
      minX = Math.min(minX, b.x);
      minY = Math.min(minY, b.y);
      maxX = Math.max(maxX, b.x + b.width);
      maxY = Math.max(maxY, b.y + b.height);
      found = true;
    }
  }
  if (!found) return false;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  const scope = ctx.scene.getPaperScope();
  ctx.commit(`Transform construction objects (${op})`, (proj) => {
    // Snapped partners outside the selection follow the transform,
    // stretching when linked at two points (#1166).
    const next: typeof proj = withSnappedPartnersFollowing(
      proj,
      projectConstructionLines(proj).map((l) => {
        if (!lineIds.has(l.id)) return l;
        switch (op) {
          // y-down project space: a positive Paper rotation turns
          // clockwise, so "rotate-right" is +90 and "rotate-left" is -90.
          case "rotate-right":
            return rotateConstructionLine(l, 90, cx, cy, scope);
          case "rotate-left":
            return rotateConstructionLine(l, -90, cx, cy, scope);
          case "flip-horizontal":
            return flipConstructionLine(l, "horizontal", cx, cy, scope);
          case "flip-vertical":
            return flipConstructionLine(l, "vertical", cx, cy, scope);
        }
      }),
      lineIds,
      scope,
    );
    if (imageIds.size > 0) {
      next.tracingImages = projectTracingImages(proj).map((img) => {
        if (!imageIds.has(img.id)) return img;
        switch (op) {
          case "rotate-right":
            return rotateTracingImage(img, 90, cx, cy);
          case "rotate-left":
            return rotateTracingImage(img, -90, cx, cy);
          case "flip-horizontal":
            return flipTracingImage(img, "horizontal", cx, cy);
          case "flip-vertical":
            return flipTracingImage(img, "vertical", cx, cy);
        }
      });
    }
    return next;
  });
  return true;
}
